/**
 * Guided workflow helpers for contractor master contract operator console.
 * System decides locks / next step; UI only surfaces the allowed primary action.
 *
 * Flow: generate → set cheque+date into PDF → approve → contractor sign
 *      → confirm physical receipt → Solarent final sign → active
 */

export const CONTRACT_STATUS = {
  Draft: 1,
  Generated: 2,
  PendingContractorSignature: 3,
  ContractorSigned: 4,
  PendingSolarentSignature: 5,
  Signed: 6,
  Active: 7,
  Superseded: 8,
  Cancelled: 9,
  ApprovedForSignature: 10,
  AwaitingGuarantee: 11,
  Expired: 12,
  PendingReview: 13,
};

export const GUARANTEE_STATUS = {
  NotSubmitted: 1,
  Submitted: 2,
  UnderReview: 3,
  Approved: 4,
  Rejected: 5,
  Expired: 6,
};

const pick = (obj, ...keys) => {
  if (!obj) return undefined;
  for (const key of keys) {
    if (obj[key] !== undefined && obj[key] !== null) return obj[key];
  }
  return undefined;
};

export const readContractFlags = (view) => {
  const status = Number(pick(view, "status", "Status") ?? 0);
  const guaranteeStatus = Number(pick(view, "guaranteeStatus", "GuaranteeStatus") ?? 0);
  const hasDoc = Boolean(
    pick(view, "hasActiveGuaranteeDocument", "HasActiveGuaranteeDocument")
  );
  const hasChequeDetails = Boolean(
    pick(view, "hasGuaranteeChequeDetails", "HasGuaranteeChequeDetails")
  );
  const physicalReceivedAt = pick(
    view,
    "physicalGuaranteeReceivedAt",
    "PhysicalGuaranteeReceivedAt"
  );

  return {
    status,
    statusTitle: pick(view, "statusTitle", "StatusTitle") || "—",
    guaranteeStatus,
    guaranteeStatusTitle:
      pick(view, "guaranteeStatusTitle", "GuaranteeStatusTitle") || "—",
    hasDoc,
    hasChequeDetails,
    physicalReceivedAt,
    contractNumber: pick(view, "contractNumber", "ContractNumber"),
    versionNumber: pick(view, "versionNumber", "VersionNumber"),
    verificationCode: pick(view, "verificationCode", "VerificationCode"),
    contractorInfoId: pick(view, "contractorInfoId", "ContractorInfoId"),
    finalPdfUrl: pick(view, "finalPdfUrl", "FinalPdfUrl"),
    signedPdfUrl: pick(view, "signedPdfUrl", "SignedPdfUrl"),
    canApproveForSignature: Boolean(
      pick(view, "canApproveForSignature", "CanApproveForSignature")
    ),
    canSendForContractorSignature: Boolean(
      pick(view, "canSendForContractorSignature", "CanSendForContractorSignature")
    ),
    canSendSolarentOtp: Boolean(
      pick(view, "canSendSolarentOtp", "CanSendSolarentOtp")
    ),
    canVerifySolarentSign: Boolean(
      pick(view, "canVerifySolarentSign", "CanVerifySolarentSign")
    ),
    canUploadGuarantee: Boolean(
      pick(view, "canUploadGuaranteeDocument", "CanUploadGuaranteeDocument")
    ),
    canViewGuarantee: Boolean(
      pick(view, "canViewGuaranteeDocument", "CanViewGuaranteeDocument")
    ),
    canSetGuaranteeChequeDetails: Boolean(
      pick(view, "canSetGuaranteeChequeDetails", "CanSetGuaranteeChequeDetails")
    ),
    canConfirmPhysicalGuaranteeReceipt: Boolean(
      pick(
        view,
        "canConfirmPhysicalGuaranteeReceipt",
        "CanConfirmPhysicalGuaranteeReceipt"
      )
    ),
    guaranteeSayadiId: pick(view, "guaranteeSayadiId", "GuaranteeSayadiId"),
    guaranteeChequeSerial: pick(
      view,
      "guaranteeChequeSerial",
      "GuaranteeChequeSerial"
    ),
    guaranteeBankName: pick(view, "guaranteeBankName", "GuaranteeBankName"),
    guaranteeAccountNumber: pick(
      view,
      "guaranteeAccountNumber",
      "GuaranteeAccountNumber"
    ),
    guaranteeBranchName: pick(view, "guaranteeBranchName", "GuaranteeBranchName"),
    guaranteeIssuerName: pick(view, "guaranteeIssuerName", "GuaranteeIssuerName"),
    guaranteeChequeDate: pick(view, "guaranteeChequeDate", "GuaranteeChequeDate"),
    guaranteeChequeDateFa: pick(
      view,
      "guaranteeChequeDateFa",
      "GuaranteeChequeDateFa"
    ),
    solarentOfficeMailingAddress: pick(
      view,
      "solarentOfficeMailingAddress",
      "SolarentOfficeMailingAddress"
    ),
    postSignMessage: pick(view, "postSignMessage", "PostSignMessage"),
  };
};

/** Pipeline stages shown to the operator (completed / current / upcoming). */
export const WORKFLOW_STEPS = [
  { id: "generate", label: "صدور قرارداد" },
  { id: "cheque", label: "ثبت چک در قرارداد" },
  { id: "approve", label: "تأیید و ارسال امضا" },
  { id: "contractorSign", label: "امضای پیمانکار" },
  { id: "physical", label: "دریافت چک فیزیکی" },
  { id: "solarentSign", label: "امضای نهایی" },
  { id: "active", label: "فعال‌سازی حساب" },
];

export const resolveStepIndex = (flags) => {
  if (!flags || !flags.status) return 0;
  const { status, hasChequeDetails, physicalReceivedAt } = flags;

  if (status === CONTRACT_STATUS.Active) return 6;
  if (
    status === CONTRACT_STATUS.PendingSolarentSignature ||
    status === CONTRACT_STATUS.Signed ||
    (status === CONTRACT_STATUS.ContractorSigned && physicalReceivedAt)
  ) {
    return 5;
  }
  if (status === CONTRACT_STATUS.ContractorSigned) return 4;
  if (status === CONTRACT_STATUS.PendingContractorSignature) return 3;
  if (
    status === CONTRACT_STATUS.ApprovedForSignature ||
    (hasChequeDetails &&
      (status === CONTRACT_STATUS.PendingReview ||
        status === CONTRACT_STATUS.Generated))
  ) {
    return 2;
  }
  if (
    status === CONTRACT_STATUS.PendingReview ||
    status === CONTRACT_STATUS.Draft ||
    status === CONTRACT_STATUS.Generated
  ) {
    return hasChequeDetails ? 2 : 1;
  }
  return 0;
};

/**
 * Single primary action the operator should take now.
 */
export const resolvePrimaryAction = (flags) => {
  if (!flags) {
    return {
      key: "load_or_generate",
      title: "شروع",
      description: "ابتدا قرارداد را بارگذاری کنید یا برای پیمانکار صادر کنید.",
      severity: "info",
    };
  }

  if (
    flags.status === CONTRACT_STATUS.Cancelled ||
    flags.status === CONTRACT_STATUS.Superseded ||
    flags.status === CONTRACT_STATUS.Expired
  ) {
    return {
      key: "terminal",
      title: "قرارداد قابل ادامه نیست",
      description: `وضعیت فعلی: ${flags.statusTitle}`,
      severity: "error",
    };
  }

  if (flags.status === CONTRACT_STATUS.Active) {
    return {
      key: "done",
      title: "قرارداد فعال است",
      description:
        "امضای نهایی انجام شده، چک تأیید شده و حساب پیمانکار تکمیل است.",
      severity: "success",
    };
  }

  if (flags.canSetGuaranteeChequeDetails && !flags.hasChequeDetails) {
    return {
      key: "set_cheque_details",
      title: "ثبت مشخصات چک در قرارداد",
      description:
        "شناسه صیادی، بانک، حساب، سریال و تاریخ چک را وارد کنید. شعبه و نام صادرکننده را پیمانکار تکمیل می‌کند.",
      severity: "warning",
      buttonLabel: "ثبت در قرارداد و به‌روزرسانی PDF",
      showChequeForm: true,
    };
  }

  if (flags.canApproveForSignature) {
    return {
      key: "approve_for_signature",
      title: "تأیید قرارداد برای امضا",
      description:
        "پس از بررسی متن قرارداد (شامل مشخصات چک)، آن را برای شروع فرایند امضا تأیید کنید.",
      severity: "info",
      buttonLabel: "تأیید برای امضا",
    };
  }

  if (flags.canSendForContractorSignature) {
    return {
      key: "send_contractor_signature",
      title: "ارسال برای امضای پیمانکار",
      description:
        "قرارداد با مشخصات چک آماده است. با این کار پیمانکار می‌تواند از پنل خود امضا کند.",
      severity: "info",
      buttonLabel: "ارسال برای امضای پیمانکار",
    };
  }

  if (flags.status === CONTRACT_STATUS.PendingContractorSignature) {
    return {
      key: "wait_contractor",
      title: "در انتظار امضای پیمانکار",
      description:
        "منتظر بمانید تا پیمانکار قرارداد را در پنل خود امضا کند.",
      severity: "warning",
    };
  }

  if (flags.canConfirmPhysicalGuaranteeReceipt) {
    return {
      key: "confirm_physical_receipt",
      title: "تأیید دریافت چک فیزیکی",
      description:
        "ابتدا عکس چک بارگذاری‌شده را ببینید؛ پس از رسیدن چک به دفتر، دریافت را تأیید کنید تا امضای نهایی باز شود.",
      severity: "warning",
      buttonLabel: "تأیید دریافت چک در دفتر",
    };
  }

  if (flags.canSendSolarentOtp || flags.canVerifySolarentSign) {
    return {
      key: "solarent_sign",
      title: "امضای نهایی سولارنت",
      description:
        "چک فیزیکی تأیید شده است. کد تأیید را ارسال و وارد کنید تا قرارداد نهایی و حساب پیمانکار تکمیل شود.",
      severity: "info",
      buttonLabel: "ارسال کد تأیید",
      needsOtp: true,
    };
  }

  // Legacy fallback for old AwaitingGuarantee contracts
  if (
    (flags.status === CONTRACT_STATUS.Signed ||
      flags.status === CONTRACT_STATUS.AwaitingGuarantee) &&
    flags.guaranteeStatus !== GUARANTEE_STATUS.Approved
  ) {
    if (!flags.hasDoc && !flags.physicalReceivedAt) {
      return {
        key: "guarantee_upload",
        title: "دریافت مدرک ضمانت (نسخه قدیمی)",
        description:
          "برای قراردادهای قدیمی، مدرک دیجیتال را آپلود یا دریافت فیزیکی را ثبت کنید.",
        severity: "warning",
        buttonLabel: flags.canUploadGuarantee ? "آپلود چک ضمانت" : null,
        allowUpload: flags.canUploadGuarantee,
      };
    }
    return {
      key: "guarantee_review",
      title: "بررسی و تأیید ضمانت",
      description: "مدرک ضمانت را بررسی و تأیید کنید.",
      severity: "info",
      buttonLabel: "تأیید ضمانت",
      allowReject: true,
      allowView: true,
      allowUpload: flags.canUploadGuarantee,
    };
  }

  if (flags.canSetGuaranteeChequeDetails) {
    return {
      key: "set_cheque_details",
      title: "ویرایش مشخصات چک",
      description: "در صورت نیاز مشخصات چک را اصلاح و PDF را به‌روز کنید.",
      severity: "info",
      buttonLabel: "به‌روزرسانی مشخصات چک",
      showChequeForm: true,
    };
  }

  return {
    key: "refresh",
    title: "وضعیت را بررسی کنید",
    description: `وضعیت فعلی: ${flags.statusTitle}. در صورت نیاز صفحه را تازه کنید.`,
    severity: "info",
  };
};
